import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { take } from 'rxjs';
import { CoreService, DialogResult, DialogService } from 'dspx-core';
import { FormConfigurationParserService } from './form-configuration-parser.service';
import { ResponseParserService } from './response-parser.service';
import { NAV_TAB_NUMBERS, PAGE_NUMBERS, QUESTION_CODES, SECTION_CODES } from "../constants/app-data.constants";

@Injectable({
  providedIn: 'root'
})
export class FormDataService {
  appData: { [key: string]: any } = {};

  constructor(
    private coreService: CoreService,
    private formConfigurationParserService: FormConfigurationParserService,
    private responseParserService: ResponseParserService,
    private dialogService: DialogService) {
  }

  private findFieldValue<T>(questionConfigs: FormlyFieldConfig[], questionKey: string, defaultValue: T = null): T {
    const field = questionConfigs.find(f => f.key === questionKey);
    const value = (field && this.coreService.getFieldValue(field) as unknown as T) || defaultValue;
    return value;
  }

  private findFieldLabel(questionConfigs: FormlyFieldConfig[], questionKey: string, defaultValue: string = null): string {
    const field = questionConfigs.find(f => f.key === questionKey);
    const label = field?.templateOptions?.label || defaultValue;
    return label;
  }

  public showOkCancelPopup(message: string, header: string, onComplete: (cancel: boolean) => void): void {
    this.dialogService
      .showSimplePopup(message, header, 'OK', 'Cancel')
      .pipe(take(1))
      .subscribe((dialogResult: DialogResult) => onComplete(dialogResult === DialogResult.REJECTED));
  }

  configureReferenceDataFormViewMode(getFormConfig: () => FormlyFieldConfig[], isViewModeAllowed: () => boolean, setViewMode: (enabled: boolean) => void, getViewMode: () => boolean) {

    this.coreService.applicationFunctions['enableViewModeForCategory'] = () => setViewMode(true);
    this.coreService.applicationFunctions['disableViewModeForCategory'] = () => setViewMode(false);
    this.coreService.applicationFunctions['showEnableViewModeButtonForCategory'] = () => isViewModeAllowed() && !getViewMode();
    this.coreService.applicationFunctions['showDisableViewModeButtonForCategory'] = () => isViewModeAllowed() && getViewMode();
    this.coreService.applicationFunctions['alwaysFalse'] = () => false; // This ensures that all the other buttons have the same "secondary" styling

    this.coreService.pageFunctions['isViewModeForCategory'] =
      (pageConfig: FormlyFieldConfig) => isViewModeAllowed() && getViewMode();

    this.coreService.pageFunctions['isSectionIncludedInViewModeForCategory'] =
      (pageConfig: FormlyFieldConfig, sectionConfig: FormlyFieldConfig) => sectionConfig.key === SECTION_CODES.referenceDataForm.viewRefDataCategory;

    this.coreService.pageFunctions['getViewModeCustomHtmlForCategory'] =
      (pageConfig: FormlyFieldConfig, sectionConfig: FormlyFieldConfig, sectionIndex: number, questionConfigs: FormlyFieldConfig[]) => {
        const referenceCategoryQuestionConfigs = this.formConfigurationParserService.findQuestionConfigs(
            getFormConfig?.(),
            NAV_TAB_NUMBERS.referenceDataForm.refDataCategory,
            PAGE_NUMBERS.referenceDataForm.refDataCategory,
            SECTION_CODES.referenceDataForm.refDataCategory)
          ?? [];

        const code = this.findFieldValue<string>(referenceCategoryQuestionConfigs, QUESTION_CODES.referenceDataForm.referenceCategoryCode, '');
        const text = this.findFieldValue<string>(referenceCategoryQuestionConfigs, QUESTION_CODES.referenceDataForm.referenceCategoryText, '');
        const isActive = this.findFieldValue<boolean>(referenceCategoryQuestionConfigs, QUESTION_CODES.referenceDataForm.referenceCategoryIsActive, null);

        const description =
          [
            [code, text].filter(item => item).join(' - '),
            isActive ? `` : `(INACTIVE)`
          ]
            .filter(item => item)
            .join(' ');
        return isActive
          ? `<div class="col-12"><h1>${description}</h1></div>`
          : `<div class="col-12"><h1><i>${description}</i></h1></div>`;
      }
  }

  referenceDataFormIsEditingRefValue(model: any, sectionIndex: number): boolean {
    const idValue = this.responseParserService.findQuestionModel(
      model,
      NAV_TAB_NUMBERS.referenceDataForm.refDataValue,
      PAGE_NUMBERS.referenceDataForm.refDataValue,
      null,
      SECTION_CODES.referenceDataForm.refDataValue,
      sectionIndex,
      'id');
    return idValue !== null && idValue !== undefined;
  }
}
